import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminAPI } from '../../services/api';
import { useApiState } from '../../hooks/useApiState';
import './SecurityEvents.css';

// Known event types (from securityLogger constants)
const KNOWN_EVENT_TYPES = [
  'login_success',
  'login_failed',
  'password_changed',
  'password_reset_self',
  'password_reset_admin',
  'password_reset_forgot',
  'password_reset_completed',
  'email_changed',
  'account_locked',
  'account_unlocked',
  'email_verified',
  'registration'
];

function SecurityEvents() {
  const navigate = useNavigate();
  const { data: events, loading, error, executeRequest } = useApiState([]);
  const [usernameSearch, setUsernameSearch] = useState('');
  const [eventTypeFilter, setEventTypeFilter] = useState('all');
  const [successFilter, setSuccessFilter] = useState('all');
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const [pagination, setPagination] = useState({ total: 0, hasMore: false });
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const usernameSearchTimeout = useRef(null);

  const loadSecurityEvents = useCallback(async (currentPage, currentPageSize, currentUsername, currentEventType, currentSuccess) => {
    try {
      const offset = currentPage * currentPageSize;
      const filters = {
        limit: currentPageSize,
        offset: offset
      };

      if (currentUsername) filters.username = currentUsername;
      if (currentEventType !== 'all') filters.eventType = currentEventType;
      if (currentSuccess !== 'all') filters.success = currentSuccess;

      await executeRequest(async () => {
        const response = await adminAPI.getSecurityEvents(filters);
        setPagination(response.data.pagination || { total: 0, hasMore: false });
        return response.data.events || [];
      });
    } catch (err) {
      console.error('Failed to load security events:', err);
    }
  }, [executeRequest]);

  // Reload when page or page size changes
  useEffect(() => {
    loadSecurityEvents(page, pageSize, usernameSearch, eventTypeFilter, successFilter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize]);

  // Immediate reload for dropdown filter changes
  useEffect(() => {
    setPage(0);
    loadSecurityEvents(0, pageSize, usernameSearch, eventTypeFilter, successFilter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventTypeFilter, successFilter]);

  // Debounced reload for username search to avoid firing on every keystroke
  useEffect(() => {
    clearTimeout(usernameSearchTimeout.current);
    usernameSearchTimeout.current = setTimeout(() => {
      setPage(0);
      loadSecurityEvents(0, pageSize, usernameSearch, eventTypeFilter, successFilter);
    }, 400);
    return () => clearTimeout(usernameSearchTimeout.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [usernameSearch]);

  const handlePageChange = (newPage) => {
    setPage(newPage);
    setExpandedRows(new Set()); // Collapse all rows when changing pages
  };

  const handlePageSizeChange = (newSize) => {
    setPageSize(Number(newSize));
    setPage(0); // Reset to first page when changing page size
    setExpandedRows(new Set());
  };

  const toggleRowExpanded = (eventId) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(eventId)) {
      newExpanded.delete(eventId);
    } else {
      newExpanded.add(eventId);
    }
    setExpandedRows(newExpanded);
  };

  const handleViewEvent = (event) => {
    setSelectedEvent(event);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedEvent(null);
  };

  const getEventTypeBadgeClass = (eventType) => {
    const typeMap = {
      'login_success': 'event-login-success',
      'login_failed': 'event-login-failed',
      'password_changed': 'event-password',
      'password_reset_self': 'event-password-reset',
      'password_reset_admin': 'event-password-reset',
      'password_reset_forgot': 'event-password-reset',
      'password_reset_completed': 'event-password-reset',
      'email_changed': 'event-email',
      'account_locked': 'event-account-locked',
      'account_unlocked': 'event-account-unlocked',
      'email_verified': 'event-email-verified',
      'registration': 'event-registration'
    };
    return typeMap[eventType] || 'event-default';
  };

  const formatEventType = (eventType) => {
    return eventType.replace(/_/g, ' ').toLowerCase()
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const formatDateTime = (timestamp) => {
    const date = new Date(timestamp);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = String(date.getFullYear()).slice(-2);
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');
    return `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`;
  };

  const renderMetadata = (metadata) => {
    if (!metadata) return null;

    const metadataObj = typeof metadata === 'string' ? JSON.parse(metadata) : metadata;

    if (Object.keys(metadataObj).length === 0) return <span className="no-data">No additional metadata</span>;

    return (
      <div className="metadata-display">
        <table className="metadata-table">
          <tbody>
            {Object.entries(metadataObj).map(([key, value]) => (
              <tr key={key}>
                <td className="metadata-key">{key}:</td>
                <td className="metadata-value">{JSON.stringify(value)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const eventTypes = ['all', ...KNOWN_EVENT_TYPES];

  if (loading && events.length === 0) {
    return (
      <div className="security-events-container">
        <div className="loading">Loading security events...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="security-events-container">
        <div className="error-container">
          <div className="error-message">{error}</div>
          <button onClick={loadSecurityEvents} className="btn-primary">
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="security-events-container">
      <div className="page-header">
        <button onClick={() => navigate('/picks')} className="btn-back">
          ← Back to Picks
        </button>
        <h1>Security Events</h1>
        <div style={{ width: '120px' }}></div> {/* Spacer for alignment */}
      </div>

      <div className="controls-bar">
        <div className="search-section">
          <input
            type="text"
            className="search-input"
            placeholder="Search by username or email..."
            value={usernameSearch}
            onChange={(e) => setUsernameSearch(e.target.value)}
          />
          {usernameSearch && (
            <button onClick={() => setUsernameSearch('')} className="clear-search">
              Clear
            </button>
          )}
        </div>

        <div className="filter-section">
          <label htmlFor="event-filter">Event Type:</label>
          <select
            id="event-filter"
            className="event-filter"
            value={eventTypeFilter}
            onChange={(e) => setEventTypeFilter(e.target.value)}
          >
            {eventTypes.map(type => (
              <option key={type} value={type}>
                {type === 'all' ? 'All Events' : formatEventType(type)}
              </option>
            ))}
          </select>
        </div>

        <div className="filter-section">
          <label htmlFor="success-filter">Status:</label>
          <select
            id="success-filter"
            className="success-filter"
            value={successFilter}
            onChange={(e) => setSuccessFilter(e.target.value)}
          >
            <option value="all">All</option>
            <option value="success">Success</option>
            <option value="failed">Failed</option>
          </select>
        </div>
      </div>

      <div className="events-table-container">
        <table className="events-table">
          <thead>
            <tr>
              <th></th>
              <th>Date & Time</th>
              <th>Event Type</th>
              <th>Username</th>
              <th className="hide-mobile">Email</th>
              <th>Success</th>
              <th className="hide-mobile">Failure Reason</th>
              <th className="view-mobile-header"></th>
            </tr>
          </thead>
          <tbody>
            {events.length === 0 ? (
              <tr>
                <td colSpan="8" className="no-results">
                  No security events found
                </td>
              </tr>
            ) : (
              events.map(event => (
                <React.Fragment key={event.id}>
                  <tr className={expandedRows.has(event.id) ? 'expanded' : ''}>
                    <td>
                      <button
                        className="expand-btn"
                        onClick={() => toggleRowExpanded(event.id)}
                        aria-label={expandedRows.has(event.id) ? 'Collapse' : 'Expand'}
                      >
                        {expandedRows.has(event.id) ? '▼' : '▶'}
                      </button>
                    </td>
                    <td className="datetime-cell">
                      {formatDateTime(event.created_at)}
                    </td>
                    <td>
                      <span className={`event-badge ${getEventTypeBadgeClass(event.event_type)}`}>
                        {formatEventType(event.event_type)}
                      </span>
                    </td>
                    <td className="username-cell">
                      {event.username || '—'}
                    </td>
                    <td className="email-cell hide-mobile">
                      {event.email || '—'}
                    </td>
                    <td>
                      <span className={`success-badge ${event.success ? 'success-yes' : 'success-no'}`}>
                        {event.success ? '✓' : '✗'}
                      </span>
                    </td>
                    <td className="failure-cell hide-mobile">
                      {event.failure_reason || '—'}
                    </td>
                    <td className="view-mobile-cell">
                      <button
                        className="btn-view-mobile"
                        onClick={() => handleViewEvent(event)}
                      >
                        View
                      </button>
                    </td>
                  </tr>
                  {expandedRows.has(event.id) && (
                    <tr className="details-row">
                      <td colSpan="8">
                        <div className="details-content">
                          <div className="detail-section">
                            <h4>User ID:</h4>
                            <p>{event.user_id || 'N/A'}</p>
                          </div>

                          {event.ip_address && (
                            <div className="detail-section">
                              <h4>IP Address:</h4>
                              <p className="ip-cell">{event.ip_address}</p>
                            </div>
                          )}

                          {event.user_agent && (
                            <div className="detail-section">
                              <h4>User Agent:</h4>
                              <p className="user-agent">{event.user_agent}</p>
                            </div>
                          )}

                          {!event.success && event.failure_reason && (
                            <div className="detail-section">
                              <h4>Failure Reason:</h4>
                              <p className="failure-reason">{event.failure_reason}</p>
                            </div>
                          )}

                          <div className="detail-section">
                            <h4>Metadata:</h4>
                            {renderMetadata(event.metadata)}
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="pagination-container">
        <div className="pagination-info">
          Showing {page * pageSize + 1}-{Math.min((page + 1) * pageSize, pagination.total)} of {pagination.total} entries
        </div>

        <div className="pagination-controls">
          <label htmlFor="page-size">Entries per page:</label>
          <select
            id="page-size"
            value={pageSize}
            onChange={(e) => handlePageSizeChange(e.target.value)}
            className="page-size-select"
          >
            <option value="25">25</option>
            <option value="50">50</option>
            <option value="100">100</option>
            <option value="200">200</option>
          </select>

          <button
            onClick={() => handlePageChange(page - 1)}
            disabled={page === 0}
            className="btn-pagination"
          >
            Previous
          </button>

          <span className="page-indicator">
            Page {page + 1} of {Math.max(1, Math.ceil(pagination.total / pageSize))}
          </span>

          <button
            onClick={() => handlePageChange(page + 1)}
            disabled={!pagination.hasMore}
            className="btn-pagination"
          >
            Next
          </button>
        </div>
      </div>

      {showModal && selectedEvent && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Security Event Details</h2>
              <button className="modal-close" onClick={closeModal}>×</button>
            </div>
            <div className="modal-body">
              <div className="detail-row">
                <span className="detail-label">Date & Time:</span>
                <span className="detail-value">{formatDateTime(selectedEvent.created_at)}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Event Type:</span>
                <span className="detail-value">
                  <span className={`event-badge ${getEventTypeBadgeClass(selectedEvent.event_type)}`}>
                    {formatEventType(selectedEvent.event_type)}
                  </span>
                </span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Username:</span>
                <span className="detail-value username-cell">{selectedEvent.username || '—'}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Email:</span>
                <span className="detail-value email-cell">{selectedEvent.email || '—'}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">User ID:</span>
                <span className="detail-value">{selectedEvent.user_id || '—'}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">IP Address:</span>
                <span className="detail-value ip-cell">{selectedEvent.ip_address || '—'}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Success:</span>
                <span className="detail-value">
                  <span className={`success-badge ${selectedEvent.success ? 'success-yes' : 'success-no'}`}>
                    {selectedEvent.success ? 'Yes ✓' : 'No ✗'}
                  </span>
                </span>
              </div>
              {!selectedEvent.success && selectedEvent.failure_reason && (
                <div className="detail-row">
                  <span className="detail-label">Failure Reason:</span>
                  <span className="detail-value failure-reason">{selectedEvent.failure_reason}</span>
                </div>
              )}
              {selectedEvent.user_agent && (
                <div className="detail-row">
                  <span className="detail-label">User Agent:</span>
                  <span className="detail-value user-agent">{selectedEvent.user_agent}</span>
                </div>
              )}
              <div className="detail-row detail-metadata">
                <span className="detail-label">Metadata:</span>
                <div className="detail-value">
                  {renderMetadata(selectedEvent.metadata)}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default SecurityEvents;
